/**
 * The four ways to look at the task list on /work: Today, Upcoming,
 * Routines and AI. Each takes the same open-task list and groups it.
 */
import { Fragment, useMemo, useState } from "react";
import { Bot, CalendarPlus, CheckCircle2, Plus, Repeat, Sparkles } from "lucide-react";
import { Card, Empty, Segmented, dotClass, dueLabel, firstName } from "@/features/command/ui";
import { cn } from "@/lib/utils";
import { GroupLabel, TaskRow } from "./TaskRow";
import { AREAS, addDays, agentName, dueDay, dueTime, fmtDay, joinDue, parseDate, relDay, repeatLabel, todayYmd } from "./lib";
import type { Person, TeamData, WorkTask } from "./types";

export interface ViewProps {
  tasks: WorkTask[];
  team: TeamData | null;
  people: Person[];
  wsTz: string;
  onToggle: (t: WorkTask) => void;
  onOpen: (t: WorkTask) => void;
  onNew: (defaults?: Partial<WorkTask>) => void;
  showOwner: boolean;
}

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
// Within each group: urgent and high first (Matthew 2026-11-04), then by time.
const byPriorityThenDue = (a: WorkTask, b: WorkTask) => {
  const da = (a.status === "completed" ? 1 : 0) - (b.status === "completed" ? 1 : 0);
  if (da) return da;
  const pr = (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2);
  if (pr) return pr;
  return (parseDate(a.due_at)?.getTime() ?? Infinity) - (parseDate(b.due_at)?.getTime() ?? Infinity);
};

function Rows({ list, p, extra }: { list: WorkTask[]; p: ViewProps; extra?: (t: WorkTask) => Partial<React.ComponentProps<typeof TaskRow>> }) {
  return (
    <>
      {list.map((t) => (
        <TaskRow key={t.id} t={t} team={p.team} people={p.people} wsTz={p.wsTz} showOwner={p.showOwner}
          onToggle={() => p.onToggle(t)} onOpen={() => p.onOpen(t)} {...(extra?.(t) ?? {})} />
      ))}
    </>
  );
}

export function TodayView(p: ViewProps) {
  const today = todayYmd();
  const late = p.tasks.filter((t) => { const d = dueDay(t); return !!d && d < today; }).sort(byPriorityThenDue);
  const now = p.tasks.filter((t) => dueDay(t) === today).sort(byPriorityThenDue);
  const none = p.tasks.filter((t) => !t.due_at).sort(byPriorityThenDue);
  const soon = p.tasks.filter((t) => { const d = dueDay(t); return !!d && d > today && d <= addDays(today, 6); }).length;

  if (!late.length && !now.length && !none.length) {
    return (
      <Card>
        <Empty icon={CheckCircle2} title="All clear for today" action={
          <button type="button" onClick={() => p.onNew({ due_at: null })} className="inline-flex h-8 items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 text-xs font-medium text-zinc-200 hover:border-indigo-400/40">
            <Plus className="h-3.5 w-3.5" /> Plan something
          </button>
        }>
          {soon ? `${soon} task${soon === 1 ? "" : "s"} coming up this week. See them under Upcoming.` : "Type a task above, or open Upcoming to plan the next two weeks."}
        </Empty>
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden pb-1.5">
      {late.length > 0 && <GroupLabel label="Late" tone="late" count={late.length} />}
      <Rows list={late} p={p} />
      {now.length > 0 && <GroupLabel label="Today" tone="today" count={now.length} />}
      <Rows list={now} p={p} />
      {none.length > 0 && (
        <>
          <GroupLabel label="No date yet" count={none.length} action={<span className="text-[11px] font-normal text-amber-300/80">give each a day or a rhythm</span>} />
          <Rows list={none} p={p} />
        </>
      )}
      {soon > 0 && (
        <div className="border-t border-zinc-800/60 px-4 pt-2.5 text-xs text-zinc-500">
          {soon} more coming up this week.
        </div>
      )}
    </Card>
  );
}

export function UpcomingView(p: ViewProps) {
  const today = todayYmd();
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => addDays(today, i + 1)), [today]);
  const last = days[days.length - 1]!;
  // Only real tasks here: the future copies of routines are not previewed
  // (Matthew 2026-11-04, "too much"); the Routines tab lists every routine.
  const total = p.tasks.filter((t) => { const d = dueDay(t); return !!d && d > today && d <= last; }).length;

  return (
    <div className="space-y-3">
      {total === 0 && (
        <Card><Empty icon={CalendarPlus} title="Nothing planned for the next two weeks">Pick a day below to add something to it.</Empty></Card>
      )}
      <Card className="overflow-hidden">
        {days.map((d, i) => {
          const list = p.tasks.filter((t) => dueDay(t) === d).sort(byPriorityThenDue);
          const weekend = [0, 6].includes(new Date(`${d}T12:00:00`).getDay());
          return (
            <Fragment key={d}>
              <div className={cn("flex items-center gap-2 px-4 pb-1 pt-3", i > 0 && "border-t border-zinc-800/50")}>
                <span className={cn("text-xs font-semibold", weekend ? "text-zinc-500" : "text-zinc-200")}>{relDay(d, today)}</span>
                {i === 0 && <span className="text-[11px] text-zinc-500">{fmtDay(d)}</span>}
                {list.length > 0 && <span className="rounded-full bg-zinc-800 px-1.5 py-px text-[10px] tabular-nums text-zinc-400">{list.length}</span>}
                <button
                  type="button"
                  onClick={() => p.onNew({ due_at: joinDue(d, "", p.wsTz) })}
                  className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                  aria-label={`Add a task on ${fmtDay(d)}`}
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
              <Rows list={list} p={p} extra={(t) => ({ showDue: !!dueTime(t, p.wsTz), dueText: { text: dueTime(t, p.wsTz), tone: "later" } })} />
              {!list.length && <div className="px-4 pb-2 text-[11px] text-zinc-600">Nothing yet</div>}
            </Fragment>
          );
        })}
      </Card>
    </div>
  );
}

export function RoutinesView(p: ViewProps) {
  const [by, setBy] = useState<"area" | "source">("area");
  const routines = p.tasks.filter((t) => t.recurrence);
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; color: string | null; list: WorkTask[] }>();
    for (const t of routines) {
      const key = by === "area" ? t.area ?? "other" : t.source_id ?? "none";
      const label = by === "area" ? AREAS.find((a) => a.value === t.area)?.label ?? "Other" : t.source_name ?? "No traffic source";
      const color = by === "area" ? AREAS.find((a) => a.value === t.area)?.color ?? "zinc" : t.source_color ?? (t.source_id ? null : "zinc");
      if (!map.has(key)) map.set(key, { key, label, color, list: [] });
      map.get(key)!.list.push(t);
    }
    const order = by === "area" ? [...AREAS.map((a) => a.value as string), "other"] : null;
    return [...map.values()].sort((a, b) => order ? order.indexOf(a.key) - order.indexOf(b.key) : (a.key === "none" ? 1 : b.key === "none" ? -1 : a.label.localeCompare(b.label)));
  }, [routines, by]);
  const noHowTo = routines.filter((t) => !t.handoff_url).length;

  if (!routines.length) {
    return (
      <Card>
        <Empty icon={Repeat} title="No routines yet" action={
          <button type="button" onClick={() => p.onNew({ recurrence: { freq: "weekly", interval: 1, days: [new Date().getDay()] } })} className="inline-flex h-8 items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 text-xs font-medium text-zinc-200 hover:border-indigo-400/40">
            <Plus className="h-3.5 w-3.5" /> Add a routine
          </button>
        }>
          A routine is work that comes back on a rhythm, like "post on Instagram every Mon, Wed, Fri". Tick it and the next one appears by itself.
        </Empty>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-500">Group by</span>
        <Segmented size="sm" value={by} onChange={setBy} options={[{ value: "area", label: "Area" }, { value: "source", label: "Traffic source" }]} />
        {noHowTo > 0 && (
          <span className="ml-auto text-[11px] text-amber-300/80">{noHowTo} of {routines.length} have no how-to yet, so only their owner can run them</span>
        )}
      </div>
      <Card className="overflow-hidden pb-1.5">
        {groups.map((g) => (
          <Fragment key={g.key}>
            <div className="flex items-center gap-2 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
              <span className={cn("h-2 w-2 rounded-full", dotClass(g.color))} />{g.label}
              <span className="rounded-full bg-zinc-800 px-1.5 py-px text-[10px] tabular-nums text-zinc-400">{g.list.length}</span>
            </div>
            <Rows list={[...g.list].sort(byPriorityThenDue)} p={p} extra={(t) => ({
              nudgeHowTo: true,
              dueText: { text: repeatLabel(t.recurrence), tone: "rhythm", sub: t.due_at ? `next: ${dueLabel(t.due_at).text}` : "no next day" },
            })} />
          </Fragment>
        ))}
      </Card>
    </div>
  );
}

export function AiView(p: ViewProps) {
  const list = p.tasks.filter((t) => t.agent_slug);
  const available = !!p.team?.ai_available;
  const groups = useMemo(() => {
    const map = new Map<string, WorkTask[]>();
    for (const t of list) map.set(t.agent_slug!, [...(map.get(t.agent_slug!) ?? []), t]);
    return [...map.entries()].sort((a, b) => (a[0] === "default" ? 1 : b[0] === "default" ? -1 : a[0].localeCompare(b[0])));
  }, [list]);

  return (
    <div className="space-y-3">
      {!available && (
        <Card className="flex items-start gap-3 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/20">
            <Bot className="h-4 w-4 text-indigo-300" />
          </div>
          <div className="text-sm">
            <div className="font-medium text-zinc-100">AI employees appear here once connected</div>
            <div className="mt-0.5 text-xs leading-relaxed text-zinc-500">
              Tasks marked for an AI wait here, owned by their person. Once this CRM is connected to SyncSuite, hand them over from the task and follow the work on the Team page.
            </div>
          </div>
        </Card>
      )}
      {list.length === 0 ? (
        <Card>
          <Empty icon={Sparkles} title="No work handed to AI yet">
            Start a task with "AI:" in the bar above (like "AI: research 5 podcasts"), or open any task and pick an AI employee to do it.
          </Empty>
        </Card>
      ) : (
        <Card className="overflow-hidden pb-1.5">
          {groups.map(([slug, rows]) => {
            const agent = p.team?.agents.find((a) => a.slug === slug);
            const boss = p.people.find((x) => x.id === agent?.manager_id);
            return (
              <Fragment key={slug}>
                <div className="flex items-center gap-2 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-indigo-200">
                  <Bot className="h-3.5 w-3.5" />{slug === "default" ? "For an AI employee (not picked yet)" : agentName(slug, p.team)}
                  {boss && <span className="font-normal normal-case tracking-normal text-zinc-500">reports to {firstName(boss.name)}</span>}
                  <span className="rounded-full bg-zinc-800 px-1.5 py-px text-[10px] tabular-nums text-zinc-400">{rows.length}</span>
                </div>
                <Rows list={[...rows].sort(byPriorityThenDue)} p={p} />
              </Fragment>
            );
          })}
        </Card>
      )}
    </div>
  );
}
